import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Drawer } from '@reckon-web/drawer';
import {
  FormValue,
  formStateToFormValue,
  getInitialState,
  isFormValueEqualToValue,
  resetForm,
  useForm,
  useSubmit,
} from '@reckon-web/forms';
import { useToasts } from '@reckon-web/toast';
import { makeId } from '@reckon-web/utils';

import { CreateTaskInput, useCreateTask } from '../../data/useCreateTask';
import { useGetTaskTypes } from '../../data/useGetTaskTypes';
import {
  GetJobAndDebtorVariables,
  useGetJobAndDebtor,
} from '../../hooks/use-get-job-and-debtor';
import { useTenantTimezoneConversion } from '../../hooks/use-tenant-timezone';
import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../work/types/task-status-enum';

import {
  TaskForm,
  TaskFormExternalErrors,
  TaskFormInitialValueInput,
  TaskFormType,
  ValidatedTaskFormValue,
  taskFormSchema,
} from './form';

export type CreateTaskOptions = {
  jobId?: string;
  status?: TaskStatusEnumValue;
};

// this type was nice, but there was a bit of a mismatch between the form field names
// and the mutation input.
// Also, it was specifically create, and the form can do create OR update
// export type CreateTaskFieldValidationErrors = Partial<
//   Record<keyof TaskCreateInput, string[]>
// >;

// This constant lets us answer "the error was in field X, where on the form is it?"
// Here is the good spot to do it, we are in control of making the mutation input
const createInputFieldsToFormFieldsMap: Record<
  keyof CreateTaskInput,
  keyof FormValue<TaskFormType> | undefined
> = {
  cloudId: undefined,
  correlationId: undefined,
  jobId: 'job',
  name: 'name',
  status: 'status',
  taskTypeId: 'taskType',
  assignedToUserId: 'assignedTo',
  description: 'description',
  dueDate: 'dueDate',
  entityId: 'client',
  estimateMinutes: 'timeEstimate',
};

export const TaskCreateDrawer: FC<{
  onClose: (
    params: { didCancel: false; newTaskId: string } | { didCancel: true }
  ) => void;
  options?: CreateTaskOptions;
  testId?: string;
}> = ({ onClose, options, testId = `task-create-drawer` }) => {
  const { localDateToTenantToUtc } = useTenantTimezoneConversion();

  // I think this one will be a fairly thin wrapper around the actual edit task control
  // the edit task control will define the form
  // but this one will do a useForm around it
  // it'll hold the form, because we really need the useSubmit
  // and in the edit version we need the isFormEqualToInitialValue
  // then, if the update works, it'll call onClose with the new task id
  // which will cause create sidebar to close, and edit sidebar to open
  const { addToast } = useToasts();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createTask] = useCreateTask();

  const getJobAndDetorArgs = useMemo(() => {
    if (!options?.jobId) {
      return undefined;
    }
    return {
      cloudId: options.jobId,
    } as GetJobAndDebtorVariables;
  }, [options]);

  const {
    loading: loadingJobAndDebtor,
    data: dataGetJobAndDebtor,
    error: errorGetJobAndDetor,
  } = useGetJobAndDebtor({
    variables: getJobAndDetorArgs,
    skip: !getJobAndDetorArgs,
  });

  // the available task types
  const {
    loading: taskTypesLoading,
    data: taskTypesData,
    error: taskTypesError,
  } = useGetTaskTypes();

  const initialFormData: TaskFormInitialValueInput | undefined = useMemo(() => {
    if (
      (getJobAndDetorArgs &&
        (loadingJobAndDebtor || errorGetJobAndDetor || !dataGetJobAndDebtor)) ||
      taskTypesLoading ||
      taskTypesError ||
      !taskTypesData
    ) {
      return undefined;
    }

    const generalTaskType = taskTypesData.find((x) => x.isAdhoc);

    const result: TaskFormInitialValueInput = {
      job: dataGetJobAndDebtor && {
        label: `${dataGetJobAndDebtor.name} (${dataGetJobAndDebtor.client.name})`,
        value: dataGetJobAndDebtor.cloudId || '',
      },
      status: options?.status || TaskStatusEnum.valuesEnum.NotStarted,
      taskType: generalTaskType?.cloudId,
    };
    return result;
  }, [
    getJobAndDetorArgs,
    loadingJobAndDebtor,
    errorGetJobAndDetor,
    dataGetJobAndDebtor,
    options,
    taskTypesLoading,
    taskTypesData,
    taskTypesError,
  ]);

  const form = useForm(taskFormSchema, initialFormData);

  const isFormEqual = isFormValueEqualToValue(
    form,
    formStateToFormValue(
      taskFormSchema,
      getInitialState(taskFormSchema, initialFormData)
    )
  );
  useEffect(
    () => {
      resetForm(form, initialFormData);
    },
    // Mitchell says this is fine because the only thing resetForm uses is form.setState
    // which doesn't depend on anything other than the schema passed to useForm which won't change
    // ideally we could pass in form.setState(and then probably use form.setState directly) but
    // form.setState isn't technically stable(as in between renders, not stable as in API, it is a stable API)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialFormData]
  );

  // hack way of overriding the dirty prompt when we save?
  // TODO see if this is actually needed
  const ignoreFormEqual = useRef(false);
  useEffect(() => {
    // make sure we reset this every time we're open
    ignoreFormEqual.current = false;
  }, []);

  const [createErrors, setCreateErrors] = useState<TaskFormExternalErrors>();

  const doSubmit = useCallback(
    async (value: ValidatedTaskFormValue) => {
      setIsSaving(true);
      // build the actual input based on what's in the form
      // TODO this could be a utility function too - it came from the template task area
      // see if Alex has anything
      let estimateMinutes: number | null = null;
      if (value.timeEstimate !== '') {
        const parsedHours = parseFloat(value.timeEstimate);
        if (!isNaN(parsedHours)) {
          estimateMinutes = parsedHours * 60;
        }
      }
      // by the time we get to here, validation has run and we KNOW that some of these values are filled in
      // so we can sling around ! and tell the compiler "I know better than you"
      const input = {
        cloudId: uuidv4(),
        jobId: value.job!.value,
        name: value.name,
        status: value.status as TaskStatusEnumValue,
        taskTypeId: value.taskType!,
        assignedToUserId: value.assignedTo,
        description: value.description,
        dueDate: localDateToTenantToUtc(value.dueDate) || null, // && parseISO(value.dueDate),
        entityId: value.client,
        estimateMinutes,
      };

      const gqlResult = await createTask({
        variables: { input },
      });

      if (
        gqlResult &&
        gqlResult.data &&
        gqlResult.data.createTask &&
        (!gqlResult.data.createTask.validationMessage ||
          gqlResult.data.createTask.validationMessage.length === 0)
      ) {
        // happy path
        // No toast, we transition to the view screen straight away

        setIsSaving(false);
        ignoreFormEqual.current = true;
        onClose({ didCancel: false, newTaskId: input.cloudId });
        return;
      }

      const validationErrors =
        gqlResult.data?.createTask?.validationMessage || [];

      const alertMessages = [];
      let hasSpecificErrors = false;
      let hasNonspecificErrors = false;
      const externalErrors: TaskFormExternalErrors = {};

      for (const message of validationErrors) {
        // look up where this particular input models property went in the field
        const formField =
          createInputFieldsToFormFieldsMap[
            message.field as keyof CreateTaskInput
          ];
        if (formField) {
          // it went somewhere, yay. Tuck away the error message for later display
          externalErrors[formField] = externalErrors[formField] || [];
          externalErrors[formField]?.push(message.message || '');
          hasSpecificErrors = true;
        } else {
          // not for any specific field we know about. Might be a cross field message, might be no field at all
          // worst case someone has renamed a field in the graphql but forgot to update the validation messages
          if (message.field && message.message) {
            alertMessages.push(`${message.field}: ${message.message}`);
          } else if (message.message) {
            alertMessages.push(message.message);
          }
          hasNonspecificErrors = true;
        }
      }

      if (!hasSpecificErrors && !hasNonspecificErrors) {
        // got nothing? Probably means the array or one of the objects carrying it itself was null
        alertMessages.push(
          `Workflow failed to create the task. Try again or contact support.`
        );
      } else {
        // got a mix, or got only specific, or got only non specific
        // any of these cases, put the same message at the end of the array (which might be empty)
        alertMessages.push('Please fix the errors and try again');
      }
      setCreateErrors(externalErrors);

      addToast({
        title: `Task not created`,
        message: alertMessages.join(),
        tone: 'critical',
      });
      setIsSaving(false);
    },
    [addToast, createTask, onClose, localDateToTenantToUtc]
  );

  const submit = useSubmit(form, doSubmit);

  const doSave = () => {
    setCreateErrors(undefined);
    submit();
  };

  return (
    <Drawer
      title="Add new task"
      type="form"
      shouldShowCancelConfirmationDialog={
        // prompt if the form is not equal (!isFormEqal) AND we haven't explicitly said to ignore the form equallity
        !isFormEqual && !ignoreFormEqual.current
      }
      actions={{
        confirm: {
          label: 'Create task',
          loading: isSaving,
          action: () => doSave(),
        },
        cancel: {
          label: 'Cancel',
          action: () => {
            // edit task stuff
            onClose({ didCancel: true });
          },
        },
      }}
      data-testid={testId}
    >
      <TaskForm
        form={form}
        mode="create"
        externalErrors={createErrors}
        testId={makeId(testId, 'task-form')}
      />
    </Drawer>
  );
};
